/**
 * The log-report kit's page parts (website roadmap S09; later log sections reuse them): a date-range picker that keeps
 * the range in the page address (?range=30d or ?from=&to=, so a report can be linked), a per-day bar chart you can zoom
 * by dragging across it, a filter row and a CSV button. Pure maths in packages/shared log-kit.ts.
 */
import { h } from '@omni-torn/ui';
import { covered, dayKey, presetOf, presetRange, RANGE_PRESETS, rangeFromQuery, rangeText, rangeToQuery, toCsv, zoomRange, type DateRange, type RangePreset } from '@omni-torn/shared';
import { box, downloadText, num, problem, short, tct } from '../ui';
import { readNote, type LogReaderLike } from './reader';
import { forgetLogs, type LogStore } from './store';
import { ownHistoryOn } from '../own-history';

/** The range the page address asks for (fallback preset when it names none). */
export function rangeFromAddress(fallback: RangePreset = '30d'): DateRange {
  return rangeFromQuery(location.search, Date.now(), fallback);
}

/** Put the range (and any other query values) in the page address, without a new history step. */
export function setAddressQuery(range: DateRange | null, extra: Record<string, string | null> = {}): void {
  let q = range ? rangeToQuery(range, Date.now(), location.search) : location.search;
  const params = new URLSearchParams(q);
  for (const [k, v] of Object.entries(extra)) v === null || v === '' ? params.delete(k) : params.set(k, v);
  q = params.toString();
  history.replaceState(history.state, '', `${location.pathname}${q ? `?${q}` : ''}${location.hash}`);
}

/** Presets (7 days … All) and a from/to pair of dates; `onPick` gets the new range. */
export function rangePicker(range: DateRange, onPick: (r: DateRange) => void): HTMLElement {
  const now = Date.now();
  const current = presetOf(range, now);
  const seg = h('div', { class: 'seg', role: 'group', 'aria-label': 'Date range' });
  for (const p of RANGE_PRESETS) {
    const b = h('button', { type: 'button', class: p.id === current ? 'active' : '', 'aria-pressed': p.id === current ? 'true' : 'false', 'data-range': p.id }, p.label);
    b.addEventListener('click', () => onPick(presetRange(p.id, Date.now())));
    seg.append(b);
  }
  const from = h('input', { type: 'date', 'aria-label': 'From', value: range.from !== null ? dayKey(range.from) : '', 'data-range-from': '' }) as HTMLInputElement;
  const to = h('input', { type: 'date', 'aria-label': 'To', value: range.to !== null ? dayKey(range.to) : '', 'data-range-to': '' }) as HTMLInputElement;
  const apply = () => {
    const q = new URLSearchParams();
    if (from.value) q.set('from', from.value);
    if (to.value) q.set('to', to.value);
    onPick(from.value || to.value ? rangeFromQuery(`?${q}`, Date.now()) : presetRange('all', Date.now()));
  };
  from.addEventListener('change', apply);
  to.addEventListener('change', apply);
  return h('div', { class: 'range-picker' }, seg, h('span', { class: 'range-dates' }, from, h('span', { class: 'muted' }, '→'), to), h('span', { class: 'muted small range-text', 'data-range-text': '' }, rangeText(range)));
}

/**
 * Bars per Torn day (values may be negative: profit). Drag across it to zoom to those days (`onZoom`); a click on one
 * bar zooms to that day. Each bar says its day and value on hover.
 */
export function zoomChart(points: ReadonlyArray<{ at: number; v: number }>, opts: { label: string; format?: (n: number) => string; onZoom?: (r: DateRange) => void; height?: number }): HTMLElement {
  const fmt = opts.format ?? short;
  if (!points.length) return h('div', { class: 'chart-empty muted' }, 'Nothing in this range yet.');
  const W = 600;
  const H = opts.height ?? 170;
  const pad = { l: 52, r: 8, t: 10, b: 22 };
  const ys = points.map((p) => p.v);
  let y0 = Math.min(0, ...ys);
  let y1 = Math.max(0, ...ys);
  if (y0 === y1) y1 = y0 + 1;
  const n = points.length;
  const bw = (W - pad.l - pad.r) / n;
  const X = (i: number) => pad.l + i * bw;
  const Y = (y: number) => pad.t + (1 - (y - y0) / (y1 - y0)) * (H - pad.t - pad.b);
  const ns = 'http://www.w3.org/2000/svg';
  const el = (tag: string, attrs: Record<string, string | number>) => {
    const e = document.createElementNS(ns, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
    return e;
  };
  const total = ys.reduce((a, b) => a + b, 0);
  const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, class: 'chart zoom-chart', role: 'img', 'aria-label': `${opts.label} per day: ${fmt(total)} over ${n} days` }) as SVGSVGElement;
  for (let i = 0; i <= 3; i++) {
    const v = y0 + ((y1 - y0) * i) / 3;
    svg.append(el('line', { x1: pad.l, x2: W - pad.r, y1: Y(v), y2: Y(v), class: 'chart-grid' }));
    const t = el('text', { x: pad.l - 6, y: Y(v) + 4, class: 'chart-axis', 'text-anchor': 'end' });
    t.textContent = fmt(v);
    svg.append(t);
  }
  const label = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  for (const [i, anchor] of [
    [0, 'start'],
    [n - 1, 'end'],
  ] as const) {
    const t = el('text', { x: anchor === 'start' ? X(0) : X(n), y: H - 6, class: 'chart-axis', 'text-anchor': anchor });
    t.textContent = label(points[i]!.at);
    svg.append(t);
  }
  const zero = Y(0);
  points.forEach((p, i) => {
    const y = Y(p.v);
    const r = el('rect', { x: X(i) + Math.min(1, bw * 0.1), y: Math.min(y, zero), width: Math.max(0.5, bw - Math.min(2, bw * 0.2)), height: Math.max(p.v ? 1 : 0, Math.abs(zero - y)), class: `chart-bar${p.v < 0 ? ' neg' : ''}`, 'data-day': dayKey(p.at) });
    const tip = el('title', {});
    tip.textContent = `${label(p.at)}: ${fmt(p.v)}`;
    r.append(tip);
    svg.append(r);
  });
  const wrap = h('div', { class: 'chart-wrap' }, svg);
  if (!opts.onZoom) return wrap;
  // Drag to zoom: a band follows the pointer; letting go zooms to the days under it.
  const band = el('rect', { x: 0, y: pad.t, width: 0, height: H - pad.t - pad.b, class: 'chart-sel', visibility: 'hidden' });
  svg.append(band);
  const dayAt = (clientX: number): number => {
    const box = svg.getBoundingClientRect();
    const x = ((clientX - box.left) / Math.max(1, box.width)) * W;
    return Math.max(0, Math.min(n - 1, Math.floor((x - pad.l) / bw)));
  };
  let start: number | null = null;
  svg.addEventListener('pointerdown', (e) => {
    start = dayAt(e.clientX);
    svg.setPointerCapture?.(e.pointerId);
    band.setAttribute('visibility', 'visible');
    band.setAttribute('x', String(X(start)));
    band.setAttribute('width', String(bw));
  });
  svg.addEventListener('pointermove', (e) => {
    if (start === null) return;
    const d = dayAt(e.clientX);
    band.setAttribute('x', String(X(Math.min(start, d))));
    band.setAttribute('width', String((Math.abs(d - start) + 1) * bw));
  });
  const end = (e: PointerEvent) => {
    if (start === null) return;
    const d = dayAt(e.clientX);
    const a = start;
    start = null;
    band.setAttribute('visibility', 'hidden');
    opts.onZoom!(zoomRange(points[Math.min(a, d)]!.at, points[Math.max(a, d)]!.at));
  };
  svg.addEventListener('pointerup', end);
  svg.addEventListener('pointercancel', () => ((start = null), band.setAttribute('visibility', 'hidden')));
  return h('div', { class: 'chart-wrap zoomable' }, svg, h('p', { class: 'muted small' }, 'Drag across the chart (or tap a day) to zoom in.'));
}

/** A "Export CSV" button: the file is made when clicked. */
export function csvButton(fileName: () => string, make: () => { header: readonly string[]; rows: ReadonlyArray<ReadonlyArray<string | number | null>> }, label = 'Export CSV'): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'btn-small', 'data-csv': '' }, label) as HTMLButtonElement;
  b.addEventListener('click', () => {
    const { header, rows } = make();
    downloadText(fileName(), toCsv(header, rows));
  });
  return b;
}

/** A labelled select for a filter row. */
export function filterSelect(label: string, options: ReadonlyArray<readonly [string, string]>, value: string, onChange: (v: string) => void, data = ''): HTMLSelectElement {
  const s = h('select', { 'aria-label': label, ...(data ? { [`data-filter`]: data } : {}) }, ...options.map(([v, t]) => h('option', { value: v, ...(v === value ? { selected: true } : {}) }, t))) as HTMLSelectElement;
  s.addEventListener('change', () => onChange(s.value));
  return s;
}

/**
 * "Your log, kept in this browser": how much is kept and read in full, a note when the range isn't read in full yet, and
 * Read newer / Read older / Forget (each a real click; the reader keeps to the minute's share). Every log report uses it.
 */
export async function keptLogPanel(o: {
  reader: LogReaderLike;
  store: LogStore;
  range: DateRange;
  /** How many kept lines this report reads, and what they are ("crimes"). */
  matching: number;
  what: string;
  /** Where read notes go (kept by the page across redraws). */
  note: HTMLElement;
  /** The question before deleting. */
  forgetText: string;
  /** After deleting: the page drops its reader (and anything else it keeps). */
  onForget: () => void;
  redraw: () => void;
}): Promise<HTMLElement> {
  const spans = await o.reader.spans();
  const complete = spans[0]?.from === 0;
  const total = await o.store.count();
  const busy = (b: HTMLButtonElement, run: () => Promise<string>) =>
    b.addEventListener('click', async () => {
      b.disabled = true;
      o.note.textContent = ' Reading…';
      try {
        o.note.textContent = await run();
      } catch (err) {
        o.note.replaceChildren(problem(err));
      }
      b.disabled = false;
      o.redraw();
    });
  const newer = h('button', { type: 'button', class: 'btn-small', 'data-action': 'log-newer' }, 'Read newer') as HTMLButtonElement;
  busy(newer, async () => readNote(await o.reader.readNewer(5)));
  const older = h('button', { type: 'button', class: 'btn-small', 'data-action': 'log-older', ...(complete ? { disabled: true } : {}) }, complete ? 'Everything read' : 'Read older') as HTMLButtonElement;
  busy(older, async () => readNote(await o.reader.readOlder(5)));
  const forget = h('button', { type: 'button', class: 'btn-small', 'data-action': 'log-forget' }, ownHistoryOn() ? 'Forget this browser’s copy' : 'Forget what’s kept here') as HTMLButtonElement;
  forget.addEventListener('click', async () => {
    if (!confirm(o.forgetText)) return;
    await forgetLogs();
    o.onForget();
    o.note.textContent = 'Deleted from this browser.';
    o.redraw();
  });
  const cover = covered(spans, o.range, Date.now());
  const onServer = ownHistoryOn();
  return box(
    onServer ? 'Your log, kept for you' : 'Your log, kept in this browser',
    [
      h('p', {}, `${num(total)} log lines ${onServer ? 'kept' : 'kept here'}, ${num(o.matching)} of them ${o.what}`, spans.length ? ` · read in full ${complete ? 'from the start' : `from ${tct(spans.at(-1)!.from)}`}` : '', '.'),
      cover < 0.999 && !complete ? h('p', { class: 'notice', 'data-range-partial': '' }, `Only ${Math.round(cover * 100)}% of the range (${rangeText(o.range)}) is read so far — “Read older” goes further back.`) : null,
      h('div', { class: 'toolbar' }, newer, older, forget, o.note),
      onServer
        ? h('p', { class: 'muted small', 'data-log-on-server': '' }, 'Kept on the Omni-Torn server for you only (a copy in this browser for speed), until you delete your data — whatever any of your devices read, so nothing is read twice. While you’re away from Omni-Torn the server reads older pages with your key, a few a minute, until Torn has nothing older. “Forget” deletes only this browser’s copy.')
        : h('p', { class: 'muted small' }, 'Only on this device: nothing here is sent anywhere. It goes past the 90 days the Omni-Torn server keeps; it stays until you delete it here (signing out deletes nothing).'),
    ],
    { id: 'log-kept' },
  );
}
